import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import ErrorPage from '~/shared/components/ErrorPage';
import LoginPage from '~/modules/auth/LoginPage';
import { AuthCallback } from '~/modules/auth/AuthCallback';
import NotFound from '~/shared/components/NotFound';
import Layout from '~/shared/components/Layout';
import { ProtectedRoute } from '~/shared/components/ProtectedRoute';
import { TreeRouteGuard } from '~/shared/components/TreeRouteGuard';
import { ROUTES } from '~/shared/constants';
import {
  ConsentScreen,
  EducationalAcknowledgmentScreen,
  QuestionScreen,
  RiskTierResultsScreen,
} from '~/modules/onboarding/pages';
import { OnboardingForm } from '~/modules/onboarding/pages/OnboardingForm';
import InvitationAcceptance from '~/modules/invitation/InvitationAcceptance';

const FamilyTreeViewer = lazy(
  () => import('~/modules/familyTree/FamilyTreeViewer'),
);

const VaultPage = lazy(() => import('~/modules/vault/VaultPage'));
const VaultEntryPage = lazy(
  () => import('~/modules/vault/pages/VaultEntryPage'),
);

const routes: RouteObject[] = [
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.AUTH_CALLBACK,
    element: <AuthCallback />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.INVITATION,
    element: <InvitationAcceptance />,
    errorElement: <ErrorPage />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: ROUTES.CONSENT,
        element: <ConsentScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: ROUTES.ONBOARDING.CONSENT,
        element: <EducationalAcknowledgmentScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: ROUTES.ONBOARDING.QUESTIONS,
        element: <QuestionScreen />,
        errorElement: <ErrorPage />,
      },
      {
        path: ROUTES.ONBOARDING.RESULTS,
        element: <RiskTierResultsScreen />,
        errorElement: <ErrorPage />,
      },
      {
        element: <Layout />,
        children: [
          {
            path: ROUTES.ONBOARDING.ROOT,
            element: <OnboardingForm />,
            errorElement: <ErrorPage />,
          },
          {
            path: ROUTES.VAULT,
            element: (
              <Suspense fallback={null}>
                <VaultPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: ROUTES.VAULT_NEW,
            element: (
              <Suspense fallback={null}>
                <VaultEntryPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            path: ROUTES.VAULT_EDIT,
            element: (
              <Suspense fallback={null}>
                <VaultEntryPage />
              </Suspense>
            ),
            errorElement: <ErrorPage />,
          },
          {
            element: <TreeRouteGuard />,
            children: [
              {
                path: ROUTES.TREE,
                element: (
                  <Suspense fallback={null}>
                    <FamilyTreeViewer />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFound />,
  },
];

export { routes };
